// Keeps every store computer and tablet on the newest STORE OS: it checks every couple of minutes
// (and whenever the tab comes back into view) whether a newer version went live. Signed out, it
// refreshes right away; signed in, it shows a bar and refreshes by itself once nothing is in
// progress (no sale in the cart, no window open, nobody typing, a minute without a tap).
import { useEffect, useRef, useState } from 'react';
import { get } from './api';

declare const __STORE_OS_BUILD__: string;
const MINE: string = typeof __STORE_OS_BUILD__ === 'string' ? __STORE_OS_BUILD__ : '';
const CHECK_MS = 2 * 60_000;
const IDLE_MS = 60_000;

/** The newer web app's build id once the server has one that isn't the one running here. */
export function useNewVersion(): string | null {
  const [newer, setNewer] = useState<string | null>(null);
  useEffect(() => {
    if (!MINE) return;
    let stopped = false;
    const check = async () => {
      try {
        const st = await get<{ version?: string | null }>('/api/status');
        if (!stopped && st.version && st.version !== MINE) setNewer(st.version);
      } catch {
        // offline or restarting: try again next time
      }
    };
    void check();
    const t = setInterval(check, CHECK_MS);
    const onShow = () => { if (document.visibilityState === 'visible') void check(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { stopped = true; clearInterval(t); document.removeEventListener('visibilitychange', onShow); };
  }, []);
  return newer;
}

/**
 * Refreshes to get the newer build, at most once every few minutes for the same build (during a
 * deploy the old and new servers can both answer for a moment; this never turns into a loop).
 */
function refreshFor(build: string): void {
  try {
    const prev = JSON.parse(sessionStorage.getItem('storeos-refresh') || 'null');
    if (prev && prev.build === build && Date.now() - prev.at < 5 * 60_000) return;
    sessionStorage.setItem('storeos-refresh', JSON.stringify({ build, at: Date.now() }));
  } catch {
    // no session storage: refresh anyway
  }
  location.reload();
}

/** Nothing would be lost by refreshing now. */
function quiet(lastTap: number, busy: boolean): boolean {
  if (busy || Date.now() - lastTap < IDLE_MS) return false;
  if (document.body.classList.contains('printing')) return false;
  if (document.querySelector('.overlay, .sheet, [role="dialog"]')) return false;
  const a = document.activeElement;
  return !(a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName));
}

/** Signed in: a bar with "Refresh now", and a refresh by itself when the store is quiet. */
export function UpdateBar({ busy }: { busy: boolean }) {
  const newer = useNewVersion();
  const lastTap = useRef(Date.now());
  useEffect(() => {
    const tap = () => { lastTap.current = Date.now(); };
    window.addEventListener('pointerdown', tap, true);
    window.addEventListener('keydown', tap, true);
    return () => { window.removeEventListener('pointerdown', tap, true); window.removeEventListener('keydown', tap, true); };
  }, []);
  useEffect(() => {
    if (!newer) return;
    const t = setInterval(() => { if (quiet(lastTap.current, busy)) refreshFor(newer); }, 10_000);
    return () => clearInterval(t);
  }, [newer, busy]);
  if (!newer) return null;
  return (
    <div className="update-bar" role="status">
      <span>A new version of STORE OS is ready.</span>
      <button type="button" onClick={() => location.reload()}>Refresh now</button>
    </div>
  );
}

/** Signed out (clock-in and sign-in screens): nothing to lose, so refresh right away. */
export function SignedOutUpdater() {
  const newer = useNewVersion();
  useEffect(() => { if (newer && !document.body.classList.contains('printing')) refreshFor(newer); }, [newer]);
  return null;
}
